/**
 * Admin Layout — V2.N.2
 *
 * 2 modes d'auth supportés en transition :
 *
 *   1. **JWT Supabase + role='admin'** (nouveau, recommandé) :
 *      Si l'utilisateur est connecté à app.edouard.ai avec un compte
 *      dont `user_profiles.role === 'admin'`, accès direct au panel.
 *      Pas de mot de passe additionnel. La session Supabase fait foi.
 *
 *   2. **ADMIN_KEY legacy** (transitoire) :
 *      Formulaire de clé d'admin, validé côté API contre les env vars
 *      ADMIN_KEY / SUPPORT_ADMIN_KEY. Sera retiré à la fin de la
 *      série V2.N.
 *
 * Au logout du compte principal, on invalide aussi l'accès admin pour
 * éviter qu'une session admin tourne après déconnexion de l'utilisateur.
 */

import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { Icons } from '../../components/Icons';
import { StorageService } from '../../services/storage';
import { useAuthStore } from '../../store/authStore';
import { useUserRole } from '../../hooks/useUserRole';
import './AdminLayout.css';
import './AdminSidebar.css';

type AuthMode = 'jwt' | 'key' | 'unauthenticated';

export function AdminLayout() {
  const { isAuthenticated: isAccountAuthenticated, user } = useAuthStore();
  const { isAdmin, isLoading: roleLoading } = useUserRole();

  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mode JWT : si le user logged-in est admin, accès auto.
  useEffect(() => {
    if (isAccountAuthenticated && isAdmin) {
      setAuthMode('jwt');
      return;
    }
    if (isAccountAuthenticated && !roleLoading && !isAdmin) {
      // User connecté mais pas admin — on tente quand même le mode key.
      // Si pas de clé stockée, on tombe sur unauthenticated.
      const storedKey = StorageService.get<string>('admin_key', '');
      if (storedKey) {
        validateAdminKey(storedKey);
      } else {
        setAuthMode('unauthenticated');
      }
      return;
    }
    if (!isAccountAuthenticated) {
      // Pas connecté du tout — fallback legacy admin_key.
      const storedKey = StorageService.get<string>('admin_key', '');
      if (storedKey) {
        validateAdminKey(storedKey);
      } else {
        setAuthMode('unauthenticated');
      }
    }
  }, [isAccountAuthenticated, isAdmin, roleLoading]);

  // Logout du compte principal → invalide aussi l'accès admin JWT.
  useEffect(() => {
    if (!isAccountAuthenticated && authMode === 'jwt') {
      setAuthMode('unauthenticated');
    }
  }, [isAccountAuthenticated, authMode]);

  const validateAdminKey = async (key: string) => {
    try {
      const response = await fetch('/api/admin?action=stats', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${key}`,
        },
      });

      if (response.ok) {
        StorageService.set('admin_key', key);
        setAuthMode('key');
        return true;
      } else {
        StorageService.remove('admin_key');
        setAuthMode('unauthenticated');
        return false;
      }
    } catch {
      StorageService.remove('admin_key');
      setAuthMode('unauthenticated');
      return false;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const isValid = await validateAdminKey(adminKey);
      if (!isValid) {
        setError('Clé admin invalide');
      }
    } catch {
      setError('Erreur de connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────
  if (authMode === null || (isAccountAuthenticated && roleLoading)) {
    return (
      <div className="admin-layout__loading">
        <Icons.Loader size={32} className="spinning" />
      </div>
    );
  }

  // ── Login form (legacy fallback OR no-permission message) ────────
  if (authMode === 'unauthenticated') {
    return (
      <div className="admin-login">
        <div className="admin-login__card">
          <div className="admin-login__header">
            <div className="admin-login__logo">
              <Icons.Shield size={32} />
            </div>
            <h1 className="admin-login__title">Administration</h1>
            <p className="admin-login__subtitle">Edouard.ai</p>
          </div>

          {/* Compte connecté mais pas admin : message clair, propose
              promotion (à faire par un admin existant). */}
          {isAccountAuthenticated && !isAdmin && (
            <div className="admin-login__error" style={{ marginBottom: '16px' }}>
              <Icons.AlertCircle size={16} />
              <span>
                Connecté en tant que <strong>{user?.email}</strong> — accès admin
                requis. Demande à un admin existant de te promouvoir.
              </span>
            </div>
          )}

          {/* Pas connecté : invite à se connecter au compte principal */}
          {!isAccountAuthenticated && (
            <div className="admin-login__error" style={{ marginBottom: '16px', background: 'rgba(105, 115, 134, 0.10)', color: 'var(--text, #1a1a1a)' }}>
              <Icons.Info size={16} />
              <span>
                Recommandé : <a href="/auth/login">connecte-toi avec ton compte</a>{' '}
                principal (admin). La clé ci-dessous est une alternative legacy.
              </span>
            </div>
          )}

          <form onSubmit={handleLogin} className="admin-login__form">
            <div className="admin-login__field">
              <label htmlFor="admin-key">Clé d'administration (legacy)</label>
              <div className="admin-login__input-wrapper">
                <Icons.Key size={18} className="admin-login__input-icon" />
                <input
                  id="admin-key"
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Entrez votre clé admin"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <div className="admin-login__error">
                <Icons.AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !adminKey}
              className="admin-login__submit"
            >
              {isSubmitting ? (
                <>
                  <Icons.Loader size={18} className="spinning" />
                  Connexion...
                </>
              ) : (
                <>
                  <Icons.LogIn size={18} />
                  Se connecter
                </>
              )}
            </button>
          </form>

          <div className="admin-login__footer">
            <a href="/" className="admin-login__back">
              <Icons.ArrowLeft size={16} />
              Retour à l'application
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Authenticated (jwt or key) ───────────────────────────────────
  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-layout__main">
        <Outlet />
      </main>
    </div>
  );
}
